import { useState, type FormEvent } from 'react'
import { Loader2, Save } from 'lucide-react'
import { toast } from 'sonner'
import { Button } from '@orisini/ui/components/button'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@orisini/ui/components/card'
import { Input } from '@orisini/ui/components/input'
import { Label } from '@orisini/ui/components/label'
import { useAuth } from '@orisini/ui/context/AuthContext'
import { api, ApiError } from '@orisini/ui/lib/api'

export default function SettingsPage() {
  const { me, refresh } = useAuth()
  const [name, setName] = useState(me?.user.name ?? '')
  const [timezone, setTimezone] = useState(me?.user.timezone ?? 'Asia/Jakarta')
  const [currentPw, setCurrentPw] = useState('')
  const [newPw, setNewPw] = useState('')
  const [busyProfile, setBusyProfile] = useState(false)
  const [busyPw, setBusyPw] = useState(false)

  const zones = (Intl as unknown as { supportedValuesOf?: (k: string) => string[] })
    .supportedValuesOf?.('timeZone') ?? ['Asia/Jakarta', 'Asia/Makassar', 'Asia/Jayapura']

  async function saveProfile(e: FormEvent) {
    e.preventDefault()
    setBusyProfile(true)
    try {
      await api.patch('/auth/profile', { name, timezone })
      await refresh()
      toast.success('Profile saved')
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : "Couldn't save the profile")
    } finally {
      setBusyProfile(false)
    }
  }

  async function changePw(e: FormEvent) {
    e.preventDefault()
    setBusyPw(true)
    try {
      await api.patch('/auth/password', { currentPassword: currentPw, newPassword: newPw })
      toast.success('Password changed — please sign in again')
      window.location.href = '/login'
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : "Couldn't change the password")
    } finally {
      setBusyPw(false)
    }
  }

  return (
    <div className="max-w-2xl space-y-6">
      <header>
        <h1 className="text-2xl font-semibold tracking-tight">Settings</h1>
      </header>

      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-base">Profile</CardTitle>
        </CardHeader>
        <CardContent>
          <form onSubmit={saveProfile} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="name">Name</Label>
              <Input id="name" value={name} onChange={(e) => setName(e.target.value)} />
            </div>
            <div className="space-y-2">
              <Label htmlFor="tz">Default timezone</Label>
              <select
                id="tz"
                value={timezone}
                onChange={(e) => setTimezone(e.target.value)}
                className="h-10 w-full rounded-md border border-input bg-background px-3 text-sm"
              >
                {zones.map((z) => (
                  <option key={z} value={z}>
                    {z}
                  </option>
                ))}
              </select>
              <p className="text-xs text-muted-foreground">
                Now:{' '}
                {new Date().toLocaleTimeString('en-GB', { timeZone: timezone, hour: '2-digit', minute: '2-digit' })}{' '}
                — used by Instagram accounts that do not set their own timezone.
              </p>
            </div>
            <Button type="submit" disabled={busyProfile}>
              {busyProfile ? <Loader2 className="animate-spin" /> : <Save />}
              Save profile
            </Button>
          </form>
        </CardContent>
      </Card>

      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-base">Change password</CardTitle>
          <CardDescription>All sessions (including agent devices) stay active; only web sessions are revoked.</CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={changePw} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="cur">Current password</Label>
              <Input id="cur" type="password" autoComplete="current-password" value={currentPw} onChange={(e) => setCurrentPw(e.target.value)} required />
            </div>
            <div className="space-y-2">
              <Label htmlFor="new">New password</Label>
              <Input id="new" type="password" autoComplete="new-password" value={newPw} onChange={(e) => setNewPw(e.target.value)} required />
            </div>
            <Button type="submit" variant="secondary" disabled={busyPw}>
              {busyPw ? <Loader2 className="animate-spin" /> : null}
              Change password
            </Button>
          </form>
        </CardContent>
      </Card>

    </div>
  )
}
